/**
 * Helpers for tracing how a part or subassembly rolls up into the assemblies /
 * subassemblies that get sold or packaged. The "Calculation Contributions" sales/package
 * log sections need to match logs for ANY ancestor in the BOM (not just direct parents),
 * and to display the chain of parents with BOM quantities.
 *
 * Implied parts are display-only on the BOM. They still appear in BOM views (BOM Check,
 * Bill of Materials, Parent/Child) but must be excluded from sales/package contribution
 * tracing, inventory session parent checks, BOM Check QR scanning, and quantity rollups
 * unless explicitly requested (e.g. Parent/Child view with "show implied" enabled).
 *
 * Data shapes (camelCase as serialized by the API):
 *  - assembly.subAssembliesToAssemblies: [{ subAssemblyFk, quantity }]
 *  - assembly.partsToAssemblies:         [{ partFk, quantity, isImpliedPart }]
 *  - subAssembly.childSubAssemblies:     [{ childSubAssemblyFk, quantity }]
 *  - subAssembly.partsToSubAssemblies:   [{ partFk, quantity, isImpliedPart }]
 */

/** @returns {boolean} */
export function isImpliedPartRelation(ref) {
  return !!(ref && ref.isImpliedPart);
}

/** @returns {boolean} True when a BOM line must be QR-scanned during BOM Check. */
export function requiresBomCheckScan(connection) {
  return !!(connection && connection.requiredForBomCheck && !isImpliedPartRelation(connection));
}

/** Part link id from a PartsToAssembly / PartsToSubAssembly row (API uses partFk). */
export function getPartLinkPartId(ref) {
  if (!ref) return null;
  return ref.partFk || ref.partId || null;
}

/**
 * Build id -> node lookup maps from the expanded arrays used across the edit screens.
 * @param {Array} assembliesArr - [{ assembly: {...} }]
 * @param {Array} subAssembliesArr - [{ subAssembly: {...} }]
 */
export function buildBomIndex(assembliesArr, subAssembliesArr) {
  const assemblyById = new Map();
  (assembliesArr || []).forEach((a) => {
    if (a && a.assembly && a.assembly.id) assemblyById.set(a.assembly.id, a.assembly);
  });
  const subById = new Map();
  (subAssembliesArr || []).forEach((s) => {
    if (s && s.subAssembly && s.subAssembly.id) subById.set(s.subAssembly.id, s.subAssembly);
  });
  return { assemblyById, subById };
}

function childSubRefs(node, type) {
  if (!node) return [];
  if (type === 'assembly') {
    return (node.subAssembliesToAssemblies || []).map((r) => ({ id: r.subAssemblyFk, qty: r.quantity }));
  }
  return (node.childSubAssemblies || []).map((r) => ({ id: r.childSubAssemblyFk, qty: r.quantity }));
}

function childPartRefs(node, type, options = {}) {
  const { includeImpliedParts = false } = options;
  if (!node) return [];
  let refs;
  if (type === 'assembly') {
    refs = (node.partsToAssemblies || []).map((r) => ({
      id: getPartLinkPartId(r),
      qty: r.quantity,
      isImpliedPart: isImpliedPartRelation(r),
    }));
  } else {
    refs = (node.partsToSubAssemblies || []).map((r) => ({
      id: getPartLinkPartId(r),
      qty: r.quantity,
      isImpliedPart: isImpliedPartRelation(r),
    }));
  }
  if (includeImpliedParts) return refs.filter((r) => r.id);
  return refs.filter((r) => r.id && !r.isImpliedPart);
}

/**
 * Does the given container (assembly or subassembly) transitively contain the target?
 * Excludes implied part links by default.
 * @param {Object} node - assembly or subassembly entity
 * @param {('assembly'|'subassembly')} type
 * @param {{id: string, kind: ('part'|'subassembly')}} target
 * @param {{assemblyById: Map, subById: Map}} index
 * @param {Set<string>} [visited]
 * @param {{includeImpliedParts?: boolean}} [options]
 */
export function containerHasTarget(node, type, target, index, visited = new Set(), options = {}) {
  if (!node || !node.id) return false;
  if (visited.has(node.id)) return false;
  visited.add(node.id);

  if (target.kind === 'part') {
    if (childPartRefs(node, type, options).some((r) => r.id === target.id)) return true;
  }

  const subRefs = childSubRefs(node, type);
  for (let i = 0; i < subRefs.length; i += 1) {
    const ref = subRefs[i];
    if (target.kind === 'subassembly' && ref.id === target.id) return true;
    const childNode = index.subById.get(ref.id);
    if (childNode && containerHasTarget(childNode, 'subassembly', target, index, visited, options)) return true;
  }
  return false;
}

/**
 * Build the chain(s) of BOM links from a sold/packaged root down to the target.
 * Excludes implied part links by default.
 *
 * @param {Object} rootNode - the assembly/subassembly referenced by the log
 * @param {('assembly'|'subassembly')} rootType
 * @param {{id: string, kind: ('part'|'subassembly'), partNumber?: string}} target
 * @param {{assemblyById: Map, subById: Map}} index
 * @param {{includeImpliedParts?: boolean}} [options]
 * @returns {Array<Array<{id:string, partNumber:string, type:string, qty:number}>>}
 */
export function buildContributionChains(rootNode, rootType, target, index, options = {}) {
  const chains = [];
  if (!rootNode) return chains;

  const dfs = (node, type, pathSoFar, visited) => {
    if (target.kind === 'part') {
      childPartRefs(node, type, options).forEach((r) => {
        if (r.id === target.id) {
          chains.push([...pathSoFar, { id: r.id, partNumber: target.partNumber || '', type: 'part', qty: r.qty }]);
        }
      });
    }

    childSubRefs(node, type).forEach((r) => {
      const childNode = index.subById.get(r.id);
      const partNumber = childNode ? (childNode.subAssemblyId || '') : '';
      const step = { id: r.id, partNumber, type: 'subassembly', qty: r.qty };

      if (target.kind === 'subassembly' && r.id === target.id) {
        chains.push([...pathSoFar, step]);
      }
      if (childNode && !visited.has(r.id)) {
        const nextVisited = new Set(visited);
        nextVisited.add(r.id);
        dfs(childNode, 'subassembly', [...pathSoFar, step], nextVisited);
      }
    });
  };

  dfs(rootNode, rootType, [], new Set([rootNode.id]));
  return chains;
}

/**
 * Parent assembly/subassembly rows for a part, excluding implied BOM links by default.
 * Used by inventory session to decide when a part row becomes visible.
 */
export function getPartParentRelations(partId, assembliesArr, subAssembliesArr, options = {}) {
  const { includeImpliedParts = false } = options;
  const parents = [];

  (assembliesArr || []).forEach((assemblyRow) => {
    const assembly = assemblyRow?.assembly;
    if (!assembly) return;
    (assembly.partsToAssemblies || []).forEach((pta) => {
      if (getPartLinkPartId(pta) !== partId) return;
      if (!includeImpliedParts && isImpliedPartRelation(pta)) return;
      parents.push({ type: 'assembly', id: assembly.id, label: assembly.assemblyId });
    });
  });

  (subAssembliesArr || []).forEach((subRow) => {
    const subAssembly = subRow?.subAssembly;
    if (!subAssembly) return;
    (subAssembly.partsToSubAssemblies || []).forEach((pts) => {
      if (getPartLinkPartId(pts) !== partId) return;
      if (!includeImpliedParts && isImpliedPartRelation(pts)) return;
      parents.push({ type: 'subassembly', id: subAssembly.id, label: subAssembly.subAssemblyId });
    });
  });

  return parents;
}

export function getPartParentConfirmationBlockers(partId, assembliesArr, subAssembliesArr, isRowConfirmed = () => false) {
  const blockers = [];
  getPartParentRelations(partId, assembliesArr, subAssembliesArr).forEach((parent) => {
    const rowKey = `${parent.type}|${parent.id}`;
    if (!isRowConfirmed(rowKey)) {
      blockers.push(`Waiting for ${parent.type} ${parent.label} to be confirmed`);
    }
  });
  return blockers;
}

/**
 * Why a part row is hidden on Inventory Session / Inventory Setup (empty = visible).
 * Implied BOM parents are ignored; only real BOM parents gate visibility.
 */
export function getPartInventorySessionBlockers(partRow, assembliesArr, subAssembliesArr, options = {}) {
  const { showNonInventoryItems = false, isRowConfirmed = () => false } = options;
  const part = partRow?.part;
  if (!part?.id) return ['Invalid part'];

  const blockers = [];

  if (!showNonInventoryItems && !part.isInventoryItem) {
    blockers.push('Not an inventory item — check "Show Non-Inventory Items" or mark the part as an inventory item in Edit Part');
  }

  blockers.push(...getPartParentConfirmationBlockers(part.id, assembliesArr, subAssembliesArr, isRowConfirmed));

  return blockers;
}

export function isPartVisibleInInventorySession(partRow, assembliesArr, subAssembliesArr, options = {}) {
  return getPartInventorySessionBlockers(partRow, assembliesArr, subAssembliesArr, options).length === 0;
}

/** @returns {boolean} True when the part only appears as an implied BOM line (no real parents). */
export function isPartOnlyImpliedInBom(partId, assembliesArr, subAssembliesArr) {
  let hasImplied = false;
  let hasNonImplied = false;

  (assembliesArr || []).forEach((assemblyRow) => {
    (assemblyRow?.assembly?.partsToAssemblies || []).forEach((pta) => {
      if (getPartLinkPartId(pta) !== partId) return;
      if (isImpliedPartRelation(pta)) hasImplied = true;
      else hasNonImplied = true;
    });
  });

  (subAssembliesArr || []).forEach((subRow) => {
    (subRow?.subAssembly?.partsToSubAssemblies || []).forEach((pts) => {
      if (getPartLinkPartId(pts) !== partId) return;
      if (isImpliedPartRelation(pts)) hasImplied = true;
      else hasNonImplied = true;
    });
  });

  return hasImplied && !hasNonImplied;
}

/** Format a quantity, dropping trailing zeros (2 -> "2", 1.5 -> "1.5"). */
export function formatBomQty(qty) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)));
}

/**
 * Human-readable parent chain, e.g. "DIF01A1 → 2× DIF01A1-SHIM".
 * @param {string} rootPartNumber
 * @param {Array<{partNumber:string, qty:number}>} chainSteps - includes the target as last element
 */
export function formatParentChain(rootPartNumber, chainSteps) {
  const ancestors = (chainSteps || []).slice(0, -1);
  let s = rootPartNumber || '';
  ancestors.forEach((step) => {
    s += ` → ${formatBomQty(step.qty)}× ${step.partNumber}`;
  });
  return s;
}

/**
 * Total per-sold-unit multiplier for a chain (product of all link quantities, including the target's).
 * @param {Array<{qty:number}>} chainSteps
 */
export function chainMultiplier(chainSteps) {
  return (chainSteps || []).reduce((acc, step) => acc * (Number(step.qty) || 0), 1);
}
